import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import { useHistory, useRouteMatch } from 'react-router-dom'
import axios from 'axios'
import {
  Box, Typography, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Chip, IconButton, TextField, MenuItem,
  Select, FormControl, InputLabel, Snackbar, Tooltip
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import Alert from '@material-ui/lab/Alert'
import AddIcon from '@material-ui/icons/Add'
import EditIcon from '@material-ui/icons/Edit'
import ArchiveIcon from '@material-ui/icons/Archive'
import VisibilityIcon from '@material-ui/icons/Visibility'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward'
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward'
import UnarchiveIcon from '@material-ui/icons/Unarchive'

const TRACK_LABELS = {
  subcontractor_setup: 'Subcontractor Setup',
  sub_portal: 'Subcontractor Portal',
  sub_mobile_app: 'Mobile App',
  field_ops: 'Field Operations',
  herbicide_queue: 'Herbicide Queue',
  site_measurement: 'Site Measurement'
}

const AUDIENCE_LABELS = {
  office: 'Office',
  admin_only: 'Admin Only',
  sub_admin: 'Sub Admin',
  applicator: 'Applicator',
  estimator: 'Estimator'
}

const STATUS_COLORS = {
  draft: { bg: '#fff3e0', color: '#e65100' },
  published: { bg: '#e8f5e9', color: '#2e7d32' },
  archived: { bg: '#f5f5f5', color: '#616161' }
}

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    maxWidth: 1200,
    margin: '0 auto'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(3)
  },
  filters: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    alignItems: 'center'
  },
  table: {
    '& th': {
      fontWeight: 600,
      backgroundColor: '#f5f5f5'
    }
  },
  statusChip: {
    fontWeight: 500,
    fontSize: '0.75rem'
  },
  trackChip: {
    fontSize: '0.7rem',
    height: 22
  },
  orderCell: {
    display: 'flex',
    alignItems: 'center',
    gap: 2
  }
}))

const ArticleManager = (props) => {
  const classes = useStyles()
  const history = useHistory()
  const { url } = useRouteMatch()
  const kbRoot = url.replace('/manage', '')

  const [articles, setArticles] = useState([])
  const [filterTrack, setFilterTrack] = useState('all')
  const [filterStatus, setFilterStatus] = useState('all')
  const [loading, setLoading] = useState(true)
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' })

  useEffect(() => {
    fetchArticles()
  }, [filterTrack, filterStatus])

  const fetchArticles = async () => {
    try {
      setLoading(true)
      const params = {}
      if (filterTrack !== 'all') params.track = filterTrack
      if (filterStatus !== 'all') params.status = filterStatus
      const { data } = await axios.get('/api/knowledgebase/articles', { params })
      setArticles(data)
    } catch (e) {
      console.error('Error fetching articles:', e)
    } finally {
      setLoading(false)
    }
  }

  const handleArchive = async (id) => {
    try {
      await axios.delete(`/api/knowledgebase/articles/${id}`)
      setSnackbar({ open: true, message: 'Article archived', severity: 'success' })
      fetchArticles()
    } catch (e) {
      setSnackbar({ open: true, message: 'Error archiving article', severity: 'error' })
    }
  }

  const handleUnarchive = async (id) => {
    try {
      await axios.put(`/api/knowledgebase/articles/${id}`, { status: 'draft' })
      setSnackbar({ open: true, message: 'Article restored to draft', severity: 'success' })
      fetchArticles()
    } catch (e) {
      setSnackbar({ open: true, message: 'Error restoring article', severity: 'error' })
    }
  }

  const handleReorder = async (article, direction) => {
    const newOrder = direction === 'up'
      ? article.trackOrder - 1
      : article.trackOrder + 1
    try {
      await axios.put(`/api/knowledgebase/articles/${article._id}`, { trackOrder: Math.max(0, newOrder) })
      fetchArticles()
    } catch (e) {
      console.error('Reorder error:', e)
    }
  }

  const handleStatusChange = async (id, newStatus) => {
    try {
      await axios.put(`/api/knowledgebase/articles/${id}`, { status: newStatus })
      setSnackbar({ open: true, message: `Status changed to ${newStatus}`, severity: 'success' })
      fetchArticles()
    } catch (e) {
      setSnackbar({ open: true, message: 'Error updating status', severity: 'error' })
    }
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => history.push(kbRoot)}>
            Back
          </Button>
          <Typography variant="h5" style={{ fontWeight: 600 }}>
            Manage Articles
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => history.push(`${kbRoot}/create`)}
        >
          New Article
        </Button>
      </Box>

      {/* Filters */}
      <Box className={classes.filters}>
        <FormControl variant="outlined" size="small" style={{ minWidth: 180 }}>
          <InputLabel>Track</InputLabel>
          <Select value={filterTrack} onChange={(e) => setFilterTrack(e.target.value)} label="Track">
            <MenuItem value="all">All Tracks</MenuItem>
            {Object.entries(TRACK_LABELS).map(([key, label]) => (
              <MenuItem key={key} value={key}>{label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl variant="outlined" size="small" style={{ minWidth: 140 }}>
          <InputLabel>Status</InputLabel>
          <Select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} label="Status">
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="draft">Draft</MenuItem>
            <MenuItem value="published">Published</MenuItem>
            <MenuItem value="archived">Archived</MenuItem>
          </Select>
        </FormControl>
        <Typography variant="body2" color="textSecondary">
          {articles.length} article{articles.length !== 1 ? 's' : ''}
        </Typography>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} variant="outlined">
        <Table className={classes.table} size="small">
          <TableHead>
            <TableRow>
              <TableCell>Order</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Track</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Audience</TableCell>
              <TableCell>Updated</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {articles.map((article) => {
              const statusStyle = STATUS_COLORS[article.status] || STATUS_COLORS.draft
              return (
                <TableRow key={article._id} hover>
                  <TableCell>
                    <Box className={classes.orderCell}>
                      <Typography variant="body2">{article.trackOrder}</Typography>
                      <IconButton size="small" onClick={() => handleReorder(article, 'up')}>
                        <ArrowUpwardIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleReorder(article, 'down')}>
                        <ArrowDownwardIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" style={{ fontWeight: 500 }}>
                      {article.title}
                    </Typography>
                    {article.summary && (
                      <Typography variant="caption" color="textSecondary" noWrap style={{ maxWidth: 300, display: 'block' }}>
                        {article.summary}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      className={classes.trackChip}
                      label={TRACK_LABELS[article.track] || article.track}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Select
                      value={article.status}
                      onChange={(e) => handleStatusChange(article._id, e.target.value)}
                      disableUnderline
                      style={{ fontSize: '0.8rem' }}
                    >
                      <MenuItem value="draft">Draft</MenuItem>
                      <MenuItem value="published">Published</MenuItem>
                      <MenuItem value="archived">Archived</MenuItem>
                    </Select>
                  </TableCell>
                  <TableCell>
                    {(article.audience || []).map(a => (
                      <Chip key={a} label={AUDIENCE_LABELS[a] || a} size="small" variant="outlined" style={{ marginRight: 2, fontSize: '0.65rem', height: 20 }} />
                    ))}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {article.updatedAt ? new Date(article.updatedAt).toLocaleDateString() : '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="View">
                      <IconButton size="small" onClick={() => history.push(`${kbRoot}/article/${article.slug}`)}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => history.push(`${kbRoot}/edit/${article._id}`)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {article.status === 'archived' ? (
                      <Tooltip title="Restore">
                        <IconButton size="small" onClick={() => handleUnarchive(article._id)}>
                          <UnarchiveIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Archive">
                        <IconButton size="small" onClick={() => handleArchive(article._id)}>
                          <ArchiveIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              )
            })}
            {articles.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={7} align="center" style={{ padding: 32 }}>
                  <Typography color="textSecondary">No articles found</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

const mapStateToProps = (state) => ({
  user: state.user,
  authenticated: state.authenticated
})

export default connect(mapStateToProps)(ArticleManager)
